import { formatSalary }
from '../utils/formatSalary';
import { useAuth } from './AuthContext';
import { redirectToJobBankLogin } from './jobBankRole';
import {
  useEffect,
  useState
} from 'react';

import {
  applyToJob
} from '../services/applicationService';

const SECTION_ORDER = [
  'OVERVIEW',
  'RESPONSIBILITIES',
  'EXPERIENCE AND SPECIALIZATION',
  'ADDITIONAL INFORMATION',
  'BENEFITS',
  'CREDENTIALS'
];

function displayDate(value) {

  if (!value) {
    return '';
  }

  if (value?.toDate) {
    return value
      .toDate()
      .toLocaleString();
  }

  return String(value);
}

function getDaysRemaining(job) {

  let expiry = null;

  // Local jobs
  if (job.expiryDate || job.expiresAt) {

    expiry =
      job.expiryDate?.toDate
        ? job.expiryDate.toDate()
        : new Date(
            job.expiryDate ||
            job.expiresAt
          );
  }

  // Canada Job Bank jobs
  else if (job.postingDate) {

    const postingDate =
      new Date(job.postingDate);

    if (!isNaN(postingDate.getTime())) {

      expiry = new Date(postingDate);

      expiry.setDate(
        expiry.getDate() + 30
      );
    }
  }

  if (!expiry || isNaN(expiry.getTime())) {
    return '';
  }

  const now = new Date();

  const diff =
    expiry.getTime() -
    now.getTime();

  const days =
    Math.ceil(
      diff / (1000 * 60 * 60 * 24)
    );

  if (days <= 0) {
    return 'Expired';
  }

  if (days === 1) {
    return '1 day remaining';
  }

  return `${days} days remaining`;
}

function getLocation(job) {

  if (job.location) {
    return job.location;
  }

  return [
    job.city,
    job.province
  ]
    .filter(Boolean)
    .join(', ');
}

function getSectionEntries(sections = {}) {

  const orderedEntries =
    SECTION_ORDER
      .filter(sectionName =>
        sections[sectionName] &&
        Object.keys(sections[sectionName]).length > 0
      )
      .map(sectionName => [
        sectionName,
        sections[sectionName]
      ]);

  const remainingEntries =
    Object.entries(sections)
      .filter(([sectionName, sectionValue]) =>
        !SECTION_ORDER.includes(sectionName) &&
        sectionValue &&
        Object.keys(sectionValue).length > 0
      );

  return [
    ...orderedEntries,
    ...remainingEntries
  ];
}

function SectionBlock({
  sectionName,
  section
}) {

  return (
    <section className="job-section-block">

      <h3>
        {sectionName}
      </h3>

      {
        Object.entries(section).map(
          ([subheading, values]) => {

            const list =
              Array.isArray(values)
                ? values
                : [values];

            const cleanList =
              list
                .map(item => String(item).trim())
                .filter(Boolean);

            if (cleanList.length === 0) {
              return null;
            }

            return (
              <div
                className="job-subsection"
                key={`${sectionName}-${subheading}`}
              >

                <h4>
                  {subheading}
                </h4>

                <ul className="job-detail-list">
                  {
                    cleanList.map((item, index) => (
                      <li
                        key={`${sectionName}-${subheading}-${index}`}
                      >
                        {item}
                      </li>
                    ))
                  }
                </ul>

              </div>
            );
          }
        )
      }

    </section>
  );
}

function buildSectionsFromLegacyJob(job) {

  const sections = {};

  if (
    job.description ||
    job.education ||
    job.experience
  ) {

    sections.OVERVIEW = {};

    if (job.description) {
      sections.OVERVIEW.Description =
        [job.description];
    }

    if (job.education) {
      sections.OVERVIEW.Education =
        [job.education];
    }

    if (job.experience) {
      sections.OVERVIEW.Experience =
        [job.experience];
    }
  }

  if (
    Array.isArray(job.responsibilities) &&
    job.responsibilities.length
  ) {

    sections.RESPONSIBILITIES = {
      Tasks: job.responsibilities
    };
  }

  if (
    Array.isArray(job.technologySkills) &&
    job.technologySkills.length
  ) {

    sections[
      'EXPERIENCE AND SPECIALIZATION'
    ] = {
      'Technology Skills':
        job.technologySkills
    };
  }

  if (
    Array.isArray(job.personalSuitability) &&
    job.personalSuitability.length
  ) {

    sections[
      'ADDITIONAL INFORMATION'
    ] = {
      'Personal Suitability':
        job.personalSuitability
    };
  }

  return sections;
}

export default function JobDetails({ job }) {

  const { currentUser } = useAuth();
  const isLoggedIn = !!currentUser;
  const visibleCompany = isLoggedIn
    ? job?.company
    : 'Sign in to view company';

  const [applying, setApplying] =
  useState(false);

  useEffect(() => {

    const panel =
      document.querySelector(
        '.job-details-panel'
      );

    if (panel) {
      panel.scrollTop = 0;
    }

  }, [job]);

  if (!job) {
    return (
      <div className="job-details empty">
        Select a job to view details.
      </div>
    );
  }

  const handleApply = async () => {

    if (!isLoggedIn) {
      redirectToJobBankLogin('/jobbankjobs');
      return;
    }

    try {

      setApplying(true);

      const result =
        await applyToJob(job);

      if (result.success) {

        alert(
          'Application submitted successfully.'
        );

      } else {

        alert(
          result.message ||
          result.error ||
          'Unable to apply.'
        );

      }

    } catch (error) {

      console.error(error);

      alert(
        'Unable to apply.'
      );

    } finally {

      setApplying(false);

    }

  };

  const sections =
  job.sections &&
  Object.keys(job.sections).length > 0
    ? job.sections
    : buildSectionsFromLegacyJob(job);

  const sectionEntries = getSectionEntries(sections);



  console.log(
    job.title,
    job.sections
  );

  return (
    <div className="job-details">

      <div className="job-card-tags">

        {
          job.source === 'jobbank'
            ? (
              <span className="jobbank-tag">
                🍁 Canada Job Bank
              </span>
            )
            : (
              <span className="local-tag">
                🏢 Local Employer
              </span>
            )
        }

      </div>

      <h2>
        {job.title}
      </h2>

      <p className="details-company">
        {visibleCompany}
      </p>

      {
        job.source === 'local' && isLoggedIn && (

          <div className="jobApplicationSection">

            <button
              className="applyJobButton"
              onClick={handleApply}
              disabled={applying}
            >

              {
                applying
                  ? 'Applying...'
                  : 'Apply Now'
              }

            </button>

          </div>

        )
      }
      {!isLoggedIn && (
        <div className="jobApplicationSection">
          <button
            className="applyJobButton"
            onClick={() => redirectToJobBankLogin('/jobbankjobs')}
          >
            Sign in to view company and apply
          </button>
        </div>
      )}

      <div className="job-meta-grid">

        <div>
          <strong>Location</strong>
          <p>
            {getLocation(job) || 'Not specified'}
          </p>
        </div>

        <div>
        <strong>
          {job.source === 'jobbank'
            ? 'Job Posting'
            : 'Salary'}
        </strong>

          {
            job.source === 'jobbank'
              ? (
                <a
                  href={job.url || job.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="jobbank-listing-button"
                >
                  Canada Job Bank
                </a>
              )
              : (
                <p>
                  {
                    job.salary
                      ? formatSalary(job.salary)
                      : 'Not specified'
                  }
                </p>
              )
          }

        </div>

        <div>
          <strong>Hours</strong>
          <p>
            {job.hoursPerWeek || 'Not specified'}
          </p>
        </div>

        <div>
          <strong>Workplace</strong>
          <p>
            {
              job.workplaceType ||
              job.type ||
              'Not specified'
            }
          </p>
        </div>

        <div>
          <strong>Posted</strong>
          <p>
            {
              displayDate(
                job.postedDate ||
                job.postingDate
              ) || 'Not specified'
            }
          </p>
        </div>

        <div>
          <strong>Time Remaining</strong>
          <p>
            {
              getDaysRemaining(job)
            }
          </p>
        </div>

      </div>



      {
        sectionEntries.length > 0
          ? (
            sectionEntries.map(
              ([sectionName, section]) => (
                <SectionBlock
                  key={sectionName}
                  sectionName={sectionName}
                  section={section}
                />
              )
            )
          )
          : (
            <section className="job-section-block">

              <h3>
                Job Details
              </h3>

              <p>
                No detailed section information is available for this posting.
              </p>

            </section>
          )
      }

      {
        job.sourceUrl && (
          <section className="job-section-block">

            <h3>
              Application Link
            </h3>

            <p>
              <a
                href={job.sourceUrl}
                target="_blank"
                rel="noreferrer"
              >
                View original job posting
              </a>
            </p>

          </section>
        )
      }
    </div>
  );
}
